<template>
  <v-list
    dense
    class="menu-static-list"
  >
    <template v-for="(section, sectionIndex) in menuSections">
      <!-- Itens da seção -->
      <template v-for="(item, itemIndex) in section.items">
        <!-- Item com children (grupo expansível) -->
        <v-list-group
          v-if="item.children"
          :key="'group-' + sectionIndex + '-' + itemIndex"
          :value="isGroupOpen(item)"
          v-access="item.access"
          no-action
        >
          <template #prependIcon>
            <component
              :is="item.icon"
              :size="20"
            />
          </template>
          <template #activator>
            <v-list-item-content>
              <v-list-item-title>{{ item.title }}</v-list-item-title>
            </v-list-item-content>
          </template>

          <v-list-item
            v-for="(child, childIndex) in item.children"
            :key="'child-' + sectionIndex + '-' + itemIndex + '-' + childIndex"
            link
            exact
            v-access="child.access"
            :to="child.to"
            @click="trackClick(child.track)"
          >
            <v-list-item-content>
              <v-list-item-title>{{ child.title }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list-group>

        <!-- Item simples (sem children) -->
        <!-- Item com link externo (href) -->
        <v-list-item
          v-else-if="item.href"
          :key="'item-' + sectionIndex + '-' + itemIndex"
          link
          v-access="item.access"
          :href="item.href"
          :target="item.target"
          @click="trackClick(item.track)"
        >
          <v-list-item-action>
            <component
              :is="item.icon"
              :size="20"
            />
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <!-- Item com rota interna (to) -->
        <v-list-item
          v-else
          :key="'route-' + sectionIndex + '-' + itemIndex"
          link
          :exact="isExactMatch(item)"
          v-access="item.access"
          :to="item.to"
          @click="trackClick(item.track)"
        >
          <!-- O ponto de alerta mora DENTRO da caixa do icone, nao solto no
               item: e o que faz ele acompanhar o icone nos dois estados da
               sidebar (expandida e trilho de 72px). -->
          <v-list-item-action>
            <span class="menu-icon">
              <component
                :is="item.icon"
                :size="20"
              />
              <span
                v-if="item.hasBadge && (hasOpenOperations || appData.pendingPreSale)"
                class="pulse-badge"
              >
                !
              </span>
            </span>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
    </template>
  </v-list>
</template>

<script>
import mixinAuthorization from '@/mixin/mixin-authorization';
import { ACCESS_ROUTES } from '@/constants/general-constants';

// Tracking
import mixpanel from 'mixpanel-browser';

export default {
  name: 'MenuStatic',
  mixins: [mixinAuthorization],
  inject: ['appData'],

  data() {
    return {
      acess: ACCESS_ROUTES,
      selectedItem: null,
      hasOpenOperations: false,
      isMini: false
    };
  },

  mounted() {
    this.$eventBus.$on('pre-sale-open-operations-loaded', this.onPreSaleOpenOperationsLoaded);
    this.detectMiniVariant();
  },

  beforeDestroy() {
    this.$eventBus.$off('pre-sale-open-operations-loaded', this.onPreSaleOpenOperationsLoaded);

    if (this._miniObserver) {
      this._miniObserver.disconnect();
    }
  },

  computed: {
    /* Todos os destinos do menu, inclusive os de dentro dos grupos. Serve para
       saber se existe um item MAIS especifico que o atual. */
    allMenuPaths() {
      return this.menuSections.flatMap((section) =>
        (section.items || []).flatMap((item) => [
          item.to,
          ...(item.children || []).map((child) => child.to)
        ])
      ).filter(Boolean);
    },

    isSuperAdmin() {
      return Number(this.appData?.currentUser?.profileId) === 1;
    },

    menuSections() {
      return [
        {
          label: 'PRINCIPAL',
          items: [
            { title: 'Home', icon: 'PhHouse', to: '/', exact: true, track: 'menu_home' },
            {
              title: 'Dashboard',
              icon: 'PhChartPie',
              to: '/dashboard',
              exact: true,
              track: 'menu_dashboard',
              access: [this.$access.MENU.DASHBOARD]
            }
          ]
        },
        {
          label: 'OPERACIONAL',
          items: [
            {
              title: 'Clientes',
              icon: 'PhUsers',
              to: '/customers',
              exact: true,
              track: 'menu_customers',
              access: [this.$access.MENU.CLIENTES]
            },
            {
              title: 'Fluxo cadastral',
              icon: 'PhClipboard',
              to: '/customers-authorizations',
              exact: false,
              track: 'menu_customer_authorizations',
              access: [this.$access.MENU.FLUXO_CADASTRAL]
            },
            {
              title: 'Operações',
              icon: 'PhCurrencyCircleDollar',
              to: '/operations',
              exact: false,
              track: 'menu_operations',
              access: [this.$access.MENU.OPERACOES]
            },
            {
              title: 'Pré-venda',
              icon: 'PhMoney',
              to: '/reports/operations/open',
              exact: false,
              track: 'menu_pre_sale',
              access: [this.$access.MENU.RASCUNHO_OPERACOES],
              hasBadge: true
            },
            // WhatsApp removido — feature desabilitada
            // {
            //   title: 'WhatsApp',
            //   icon: 'PhChatCircleDots',
            //   to: '/whatsapp-inbox',
            //   exact: true,
            //   track: 'menu_whatsapp_inbox',
            //   access: [this.$access.MENU.WHATSAPP_INBOX]
            // }
          ]
        },
        {
          label: 'RELATÓRIOS',
          items: [
            {
              title: 'Relatórios operacionais',
              icon: 'PhFileText',
              track: 'menu_relatorios_operacionais',
              access: [
                this.$access.MENU.RELATORIOS_OPERACIONAIS.REMESSAS_INTERNACIONAIS,
                this.$access.MENU.RELATORIOS_OPERACIONAIS.LUCRO_REMESSAS_INTERNACIONAIS,
                this.$access.MENU.RELATORIOS_OPERACIONAIS.CAMBIO_TURISMO,
                this.$access.MENU.RELATORIOS_OPERACIONAIS.LUCRO_CAMBIO_TURISMO
              ],
              children: [
                {
                  title: 'Remessas internacionais',
                  to: '/reports/remittance',
                  track: 'menu_remittance_operations_reports',
                  access: [this.$access.MENU.RELATORIOS_OPERACIONAIS.REMESSAS_INTERNACIONAIS]
                },
                {
                  title: 'Lucro / Remessas internacionais',
                  to: '/reports/remittance/management',
                  track: 'menu_remittance_management_operations_reports',
                  access: [this.$access.MENU.RELATORIOS_OPERACIONAIS.LUCRO_REMESSAS_INTERNACIONAIS]
                },
                {
                  title: 'Câmbio turismo',
                  to: '/reports/exchange',
                  track: 'menu_exchange_operations_reports',
                  access: [this.$access.MENU.RELATORIOS_OPERACIONAIS.CAMBIO_TURISMO]
                }
              ]
            },
            {
              title: 'Relatórios Stablecoin',
              icon: 'PhCurrencyBtc',
              track: 'menu_relatorios_stablecoin',
              access: [
                this.$access?.STABLECOIN?.RELATORIOS?.REMESSAS,
                this.$access?.STABLECOIN?.RELATORIOS?.LUCRO_REMESSAS
              ],
              children: [
                {
                  title: 'Remessas internacionais',
                  to: '/reports/stablecoin/remittance',
                  track: 'menu_stablecoin_remittance_reports',
                  access: [this.$access?.STABLECOIN?.RELATORIOS?.REMESSAS]
                },
                {
                  title: 'Lucro / Remessas internacionais',
                  to: '/reports/stablecoin/management',
                  track: 'menu_stablecoin_management_reports',
                  access: [this.$access?.STABLECOIN?.RELATORIOS?.LUCRO_REMESSAS]
                }
              ]
            }
          ]
        },
        {
          label: 'CADASTROS',
          items: [
            {
              title: 'Gestão de DUIMP/DU-E',
              icon: 'PhFileSearch',
              to: '/reports/import-data/management',
              exact: false,
              track: 'menu_duimp_due',
              access: [this.$access.MENU.GESTAO_DUIMP_DUE]
            },
            {
              title: 'Corban',
              icon: 'PhBriefcase',
              track: 'menu_corban',
              access: [this.$access.MENU.CORBAN.CADASTROS, this.$access.MENU.CORBAN.COMISSOES],
              children: [
                {
                  title: 'Cadastros',
                  to: '/correspondents',
                  track: 'menu_correspondents',
                  access: [this.$access.MENU.CORBAN.CADASTROS]
                },
                {
                  title: 'Comissões',
                  to: '/operations/commissions',
                  track: 'menu_commissions',
                  access: [this.$access.MENU.CORBAN.COMISSOES]
                }
              ]
            },
            {
              title: 'Cadastros operacionais',
              icon: 'PhPlusCircle',
              track: 'menu_cadastros_operacionais',
              access: [
                this.$access.MENU.CADASTROS_OPERACIONAIS.NATUREZAS_OPERACAO,
                this.$access.MENU.CADASTROS_OPERACIONAIS.BANCOS,
                this.$access.MENU.CADASTROS_OPERACIONAIS.SPREADS_MOEDAS,
                this.$access.MENU.CADASTROS_OPERACIONAIS.METODOS_PAGAMENTO,
                this.$access.MENU.CADASTROS_OPERACIONAIS.APIS_COTACAO,
                this.$access.MENU.CADASTROS_OPERACIONAIS.TEMPLATES,
                this.$access.MENU.CADASTROS_OPERACIONAIS.FERIADOS
              ],
              children: [
                {
                  title: 'Naturezas de operação',
                  to: '/registrations/operation-natures',
                  track: 'menu_registrations_operation_nature',
                  access: [this.$access.MENU.CADASTROS_OPERACIONAIS.NATUREZAS_OPERACAO]
                },
                {
                  title: 'Bancos',
                  to: '/registrations/banks',
                  track: 'menu_banks',
                  access: [this.$access.MENU.CADASTROS_OPERACIONAIS.BANCOS]
                },
                {
                  title: 'Spread / Moedas',
                  to: '/registrations/currencies',
                  track: 'menu_registrations_currencies',
                  access: [this.$access.MENU.CADASTROS_OPERACIONAIS.SPREADS_MOEDAS]
                },
                {
                  title: 'Métodos de pagamento',
                  to: '/registrations/payment-methods',
                  track: 'menu_registrations_payment_methods',
                  access: [this.$access.MENU.CADASTROS_OPERACIONAIS.METODOS_PAGAMENTO]
                },
                {
                  title: 'Apis de cotação',
                  to: '/registrations/rate-apis',
                  track: 'menu_registrations_rate_apis',
                  access: [this.$access.MENU.CADASTROS_OPERACIONAIS.APIS_COTACAO]
                },
                {
                  title: 'Templates',
                  to: '/registrations/templates',
                  track: 'menu_registrations_templates',
                  access: [this.$access.MENU.CADASTROS_OPERACIONAIS.TEMPLATES]
                },
                {
                  title: 'Feriados',
                  to: '/registrations/currency-holidays',
                  track: 'menu_registrations_currency_holidays',
                  access: [this.$access.MENU.CADASTROS_OPERACIONAIS.FERIADOS]
                }
              ]
            }
          ]
        },
        {
          label: 'ADMINISTRAÇÃO',
          items: [
            {
              title: 'Usuários',
              icon: 'PhUser',
              to: '/users',
              exact: true,
              track: 'menu_users',
              access: [this.$access.MENU.USUARIOS]
            },
            {
              title: 'Perfis',
              icon: 'PhIdentificationCard',
              to: '/profiles',
              exact: true,
              track: 'menu_profiles',
              access: [this.$access.MENU.PERFIS],
              hidden: !this.isSuperAdmin
            },
            {
              title: 'Auditoria',
              icon: 'PhShieldCheck',
              to: '/audit',
              exact: true,
              track: 'menu_audit',
              access: [this.$access.MENU.AUDITORIA]
            }
          ]
        },
        {
          label: 'OUTROS',
          items: [
            {
              title: 'Gerar Invoices',
              icon: 'PhReceipt',
              href: 'https://invoices.techcambio.com.br',
              target: '_blank',
              track: 'menu_gerar_invoices',
              access: [this.$access.MENU.GERAR_INVOICES]
            },
            {
              title: 'Fechamento mensal',
              icon: 'PhCalendarCheck',
              to: '/exchange-closing',
              exact: false,
              track: 'menu_exchange_closing',
              access: [this.$access.MENU.RELATORIOS_OPERACIONAIS.CONTA_CORRENTE_BANCOS]
            }
          ]
        }
      ].map((section) => ({
        ...section,
        items: section.items.filter((item) => !item.hidden)
      }));
    }
  },

  methods: {
    trackClick(buttonName) {
      if (!buttonName) return;

      this.$nextTick(() => {
        mixpanel.track('click', {
          button_name: buttonName
        });
      });
    },

    onPreSaleOpenOperationsLoaded({ hasOpenOperations }) {
      this.hasOpenOperations = !!hasOpenOperations;
    },

    /* `exact: false` existe para o item continuar aceso nas telas filhas: em
       `/operations/123` quem deve estar ativo e "Operacoes". Mas o casamento
       por PREFIXO do router-link nao sabe parar quando existe um item de menu
       mais especifico — em `/operations/commissions` acendiam os dois, o pai e
       "Comissoes" dentro de Corban.

       Aqui o prefixo so vale quando NENHUM path mais especifico do menu
       casa com a rota atual. Vale para qualquer par assim; hoje sao dois
       (`/operations` e `/reports/remittance`). */
    isExactMatch(item) {
      if (item.exact !== false) return true;

      return this.allMenuPaths.some(
        (path) =>
          path !== item.to &&
          path.startsWith(item.to) &&
          this.$route.path.startsWith(path)
      );
    },

    isGroupOpen(item) {
      if (!item.children) return false;

      return item.children.some((child) => this.$route.path.startsWith(child.to));
    },

    detectMiniVariant() {
      const drawer = this.$el.closest('.v-navigation-drawer');

      if (!drawer) return;

      const update = () => {
        this.isMini = drawer.classList.contains('v-navigation-drawer--mini-variant');
      };

      update();
      this._miniObserver = new MutationObserver(update);
      this._miniObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }
  }
};
</script>

<style lang="scss" scoped>
.section-label {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.38);
  padding: 16px 16px 4px;
  user-select: none;
}

.theme--dark .section-label {
  color: rgba(255, 255, 255, 0.38);
}

/* Caixa de referencia do ponto de alerta: 20px, o tamanho do proprio icone.
   O ponto se ancorava no `.v-list-item` (unico ancestral posicionado) com
   coordenadas absolutas, e a sidebar tem DUAS geometrias — expandida, com o
   item ocupando a largura toda, e o trilho de 72px, onde o item e um quadrado
   de 44px. Um `left: 28px` medido da borda do item cai em lugares diferentes
   nos dois estados: no trilho o ponto saia 2px para FORA do quadrado.

   Ancorado ao icone, o mesmo par de coordenadas vale nos dois estados — e e
   isso que dispensa o `left: -4px` que existia no icone para abrir espaco ao
   ponto. Aquele empurrao tirava o item de Pre-venda da coluna de icones do
   menu (x=36, ver design-system/styles/vuetify.css §9.1): com o alerta ligado
   o icone ficava 4px a esquerda de todos os outros. */
.menu-icon {
  position: relative;
  display: inline-flex;
  width: 20px;
  height: 20px;
}

.pulse-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  /* O anel recorta o ponto do fundo da sidebar, que e escura nos dois temas
     (--tc-shell). Com --v-backgroundPrimary-base — o fundo CLARO da pagina —
     sobrava um halo branco em volta do ponto. */
  border: 2px solid var(--tc-shell);
  background: var(--v-error-base);
  color: #ffffff;
  font-size: 10px;
  font-weight: bold;
  line-height: 1;
  cursor: pointer;
  box-shadow: none;
  animation: pulse 2s infinite;
}

.pulse-badge:hover {
  animation: none;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 82, 82, 0.6);
  }
  70% {
    box-shadow: none;
  }
  100% {
    box-shadow: none;
  }
}
</style>
